import React, { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Grid,
  Card,
  Typography,
  Stack,
  Button,
  Paper,
  TextField,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableFooter,
  Pagination,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Alert,
  DialogActions,
  DialogContentText,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import Iconify from 'src/components/iconify';
import ReactApexChart from 'react-apexcharts';
import SearchIcon from '@mui/icons-material/Search';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Services from 'src/services/Services';
import api from 'src/services/BaseService';
import { useSelector } from 'react-redux';
import HeatmapLegend from 'src/utils/mallManagement/HeatMapLegend';
import { toast } from 'react-toastify';
import { SERVICES } from 'src/utils/servicePermissions';
import useServicePermission from 'src/hooks/useServicePermission';

const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_MAP_TR = { "monday": "P.tesi", "tuesday": "Salı", "wednesday": "Çarş.", "thursday": "Perş.", "friday": "Cuma", "saturday": "C.tesi", "sunday": "Pazar" };

export default function MallManagementHandsFree() {
  const parsedUser = JSON.parse(localStorage.getItem('user'));
  const customer = useSelector((state) => state.customerReducer);
  const mallID = customer?.customer?.id;
  const theme = useTheme();

  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasPermission, setHasPermission] = useState(false);
  const [showApplyBanner, setShowApplyBanner] = useState(false);
  const [openApplyDialog, setOpenApplyDialog] = useState(false);

  // Check service permission using hook
  const { hasPermission: handsfreePermission, loading: permissionLoading } = useServicePermission(SERVICES.HANDSFREE);

  const [handsfreeList, setHandsfreeList] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [startDate, setStartDate] = useState(moment());
  const [endDate, setEndDate] = useState(moment());
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [isVideoModalOpen, setVideoModalOpen] = useState(false);

  const handleOpenVideoModal = () => setVideoModalOpen(true);
  const handleCloseVideoModal = () => setVideoModalOpen(false);

  const handleApplyClick = () => setOpenApplyDialog(true);
  const handleCloseDialog = () => setOpenApplyDialog(false);
  const handleConfirmApply = async () => {
    try {
      const mallsResponse = await api.post('/malls/get-malls', { withOffices: true });

      if (mallsResponse.data && mallsResponse.data.status) {
        const foundMall = mallsResponse.data.result.find((item) => item.id === mallID);
        const mallTitle = foundMall?.title || 'Bilinmeyen AVM';

        const formData = {
          mallName: mallTitle,
          mallID: mallID,
          userName: parsedUser?.name || parsedUser?.email || 'Bilinmeyen Kullanıcı',
          serviceName: 'Hands Free',
        };

        const response = await Services.createContactForm(parsedUser.token, formData);

        if (response.data && response.data.status) {
          toast.success('Talebiniz başarıyla iletilmiştir.');
          handleCloseDialog();
        } else {
          toast.error(response.data?.message || 'Talep gönderilirken bir hata oluştu.');
        }
      } else {
        toast.error('AVM bilgileri alınamadı.');
      }
    } catch (error) {
      console.error('Error submitting service request:', error);
      toast.error('Talep gönderilirken bir hata oluştu.');
    }
  };

  useEffect(() => {
    if (permissionLoading) {
      return;
    }
    const getLiveAnalytics = async () => {
      if (!mallID) { setLoading(false); return; }
      setLoading(true);
      try {
        const userHasPermission = handsfreePermission;
        setHasPermission(userHasPermission);
        if (userHasPermission) {
          const response = await Services.GetLiveAnalyticsHandsfree(parsedUser.token, mallID);
          if (response.data && response.data.result) {
            setAnalyticsData(response.data.result);
            setHandsfreeList(response.data.result.handsfreeList || []);
          } else {
            setAnalyticsData({});
            setHandsfreeList([]);
          }
        } else {
          setAnalyticsData({});
          setHandsfreeList([]);
          setShowApplyBanner(true);
        }
      } catch (error) {
        console.error("Veri yüklenirken hata oluştu:", error);
        setAnalyticsData({});
        setHandsfreeList([]);
      } finally {
        setLoading(false);
      }
    };
    getLiveAnalytics();
  }, [mallID, parsedUser?.token, handsfreePermission, permissionLoading]);

  const getLiveAnalyticsHandsfreeList = () => {
    if (!hasPermission) return;
    const startDateFormatted = startDate.format('YYYY-MM-DD');
    const endDateFormatted = endDate.format('YYYY-MM-DD');
    Services.GetLiveAnalyticsHandsfreeList(parsedUser.token, mallID, startDateFormatted, endDateFormatted)
      .then((res) => {
        setHandsfreeList(res.data.result || []);
      })
      .catch((err) => console.error('HandsfreeList yüklenirken hata:', err));
  };

  const handleClearFilters = () => {
    if (!hasPermission) return;
    setFilterText('');
    setStartDate(moment());
    setEndDate(moment());
    setHandsfreeList(analyticsData?.handsfreeList || []);
  };

  useEffect(() => {
    setPage(1);
  }, [filterText, handsfreeList]);

  const zeroOutSeries = (series) => {
    if (!Array.isArray(series)) return [];
    const firstEl = series[0];
    if (typeof firstEl === 'number') { return series.map(() => 0); }
    if (typeof firstEl === 'object' && firstEl !== null && 'data' in firstEl) {
      return series.map(s => ({ ...s, data: Array.isArray(s.data) ? s.data.map(() => 0) : [] }));
    }
    return [];
  };

  // --- GÜNCELLENEN useMemo MANTIĞI ---
  const charts = useMemo(() => {
    const data = analyticsData || {};
    const priceList = data.priceList || {};

    // --- FİYAT HESAPLAMA MANTIĞI ---
    const calculatePriceRange = (priceArray) => {
      if (!priceArray || priceArray.length === 0) {
        return { min: null, max: null };
      }
      const prices = priceArray.map(item => item.price).filter(p => typeof p === 'number');
      if (prices.length === 0) {
        return { min: null, max: null };
      }
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      return { min, max };
    };

    const formatPriceDisplay = ({ min, max }) => {
      if (min === null || max === null) {
        return 'N/A'; // Bu 'N/A' sadece veri var ama fiyat yoksa görünür, normalde satır gizlenir.
      }
      if (min === max) {
        return `${min} ₺`;
      }
      return `${min} ₺ - ${max} ₺`;
    };

    // 1. Nokta Fiyatı (points)
    const pointsPrices = priceList.points || [];
    const pointsRange = calculatePriceRange(pointsPrices);

    // 2. Predefined Fiyatı (predefined)
    const predefinedItems = priceList.predefined || [];
    const groupedPredefined = predefinedItems.reduce((acc, item) => {
      const { type, price, currency } = item;
      if (!type || price === undefined) return acc;
      if (!acc[type]) acc[type] = [];
      acc[type].push({ price, currency });
      return acc;
    }, {});
    const predefinedRangesByType = Object.keys(groupedPredefined).map(type => {
      const range = calculatePriceRange(groupedPredefined[type]);
      return {
        type: type,
        display: formatPriceDisplay(range)
      };
    });

    // 3. Postbox Fiyatı (postbox)
    const postboxPrices = priceList.postbox || [];
    const postboxRange = calculatePriceRange(postboxPrices);

    // 4. Local Delivery Fiyatı (localDelivery)
    const localDeliveryData = priceList.localDelivery || {};
    const localDeliveryPrices = Object.values(localDeliveryData)
      .map(district => ({ price: district.price, currency: district.currency }))
      .filter(item => item.price !== undefined);
    const localDeliveryRange = calculatePriceRange(localDeliveryPrices);

    // Fiyatları ve VARLIK KONTROLLERİNİ bir objede topluyoruz
    const priceRanges = {
      points: formatPriceDisplay(pointsRange),
      predefined: predefinedRangesByType, // Bu zaten bir dizi, .length ile kontrol edilecek
      postbox: formatPriceDisplay(postboxRange),
      localDelivery: formatPriceDisplay(localDeliveryRange),

      // --- YENİ EKLENEN BAYRAKLAR ---
      // Verinin priceList içinde var olup olmadığını ve boş olmadığını kontrol et
      hasPoints: pointsPrices.length > 0,
      hasPostbox: postboxPrices.length > 0,
      hasLocalDelivery: localDeliveryPrices.length > 0, // localDeliveryPrices'i kontrol etmemiz yeterli
    };
    
    // --- ESKİ GRAFİK MANTIĞI DEVAM EDİYOR ---
    const now = new Date();
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const commonBarPlot = { bar: { columnWidth: '20%', borderRadius: 8, borderRadiusApplication: 'end' } };
    const deskBarPlot = { bar: { columnWidth: '30%', borderRadius: 8, borderRadiusApplication: 'end' } };
    const deliveryTypesMap = { hotel: 'Otel', easypoint: 'Easy Point', residance: 'Rezidans', address: 'Adrese Teslim', box: 'Emanet Dolabı', other: 'Diğer' };

    const monthlyDeliveryType = data.monthlyDeliveryType || {};
    const monthlyDeliveryCategories = ['1-7', '8-14', '15-21', `22-${lastDayOfMonth}`];
    const monthlyDeliveryChart = {
      series: Object.keys(monthlyDeliveryType).map((key) => ({
        name: deliveryTypesMap[key] || key,
        data: monthlyDeliveryCategories.map((cat) => monthlyDeliveryType[key]?.[cat] || 0),
      })),
      options: { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: commonBarPlot, colors: ['#1C64F2', '#14B8A6', '#E74694', '#FDBA8C', '#FDBA2C', '#8C52FF'], xaxis: { categories: monthlyDeliveryCategories }, yaxis: { title: { text: 'Toplam Taşıma Adedi' }, labels: { formatter: (val) => val.toFixed(0) } }, legend: { position: 'bottom' }, dataLabels: { enabled: false } },
    };

    const monthlyUsageData = data.monthlyUsage || {};
    const monthlyUsageCategories = ['1-7', '8-14', '15-21', `22-${lastDayOfMonth}`];
    const monthlyUsageChart = {
      series: [
        { name: 'Kullanım Adedi', type: 'column', data: monthlyUsageCategories.map((cat) => monthlyUsageData.total?.[cat] || 0) },
        { name: 'Ciro', type: 'line', data: monthlyUsageCategories.map((cat) => monthlyUsageData.income?.[cat] || 0) },
      ],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 3] },
        colors: ['#1C64F2', '#14B8A6'],
        plotOptions: commonBarPlot,
        xaxis: { categories: monthlyUsageCategories },
        yaxis: [
          {
            title: { text: 'Kullanım Adedi' },
            labels: {
              formatter: (val) => val.toFixed(0)
            }
          },
          {
            opposite: true,
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`
            }
          }
        ],
        legend: { position: 'bottom' },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 1) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      },
    };

    const weeklyDeliveryType = data.weeklyDeliveryType || {};
    const startOfWeek = moment().startOf('isoWeek');
    const weeklyCategories = Array.from({ length: 7 }, (_, i) => startOfWeek.clone().add(i, 'days').format('MMM DD'));
    const weeklyDateKeys = Array.from({ length: 7 }, (_, i) => startOfWeek.clone().add(i, 'days').format('YYYY-MM-DD'));
    const weeklyDeliveryChart = {
      series: Object.keys(deliveryTypesMap).map(key => ({
        name: deliveryTypesMap[key],
        data: weeklyDateKeys.map(d => weeklyDeliveryType[key]?.[d] || 0),
      })),
      options: { chart: { type: 'bar', stacked: true, toolbar: { show: false } }, plotOptions: deskBarPlot, dataLabels: { enabled: false }, colors: ['#1C64F2', '#14B8A6', '#E74694', '#FDBA8C', '#FDBA2C', '#8C52FF'], xaxis: { categories: weeklyCategories }, yaxis: { labels: { formatter: (val) => val.toFixed(0) } }, legend: { position: 'bottom' } },
    };

    const weeklyUsage = data.weeklyUsage || {};
    const weeklyUsageChart = {
      series: [
        { name: 'Kullanım Adedi', type: 'column', data: weeklyDateKeys.map((d) => weeklyUsage[d]?.usageCount || 0) },
        { name: 'Ciro', type: 'line', data: weeklyDateKeys.map((d) => weeklyUsage[d]?.totalPrice || 0) },
      ],
      options: {
        chart: { stacked: false, toolbar: { show: false } },
        stroke: { width: [0, 3] },
        colors: ['#1C64F2', '#14B8A6'],
        plotOptions: deskBarPlot,
        xaxis: { categories: weeklyCategories },
        legend: { position: 'bottom' },
        yaxis: [
          {
            title: { text: 'Kullanım Adedi' },
            labels: {
              formatter: (val) => val.toFixed(0)
            }
          },
          {
            opposite: true,
            title: { text: 'Ciro' },
            labels: {
              formatter: (val) => `${val.toFixed(0)} ₺`
            }
          }
        ],
        tooltip: {
          y: {
            formatter: (val, { seriesIndex }) => {
              if (seriesIndex === 1) {
                return `${val.toFixed(2)} ₺`;
              }
              return `${val.toFixed(0)} adet`;
            }
          }
        }
      },
    };

    const hourlyUsageData = data.monthlyDailyHourlyUsageTotalCount ?? {};
    const allTimeSlots = Array.from({ length: 16 }, (_, i) => { const h = 8 + i, n = h + 1; return `${h.toString().padStart(2, '0')}:00-${n === 24 ? '00' : n.toString().padStart(2, '0')}:00`; });

    const hourlyTotals = allTimeSlots.map(timeSlot => {
      return DAY_ORDER.reduce((sum, dayKey) => sum + (hourlyUsageData[dayKey]?.[timeSlot] || 0), 0);
    });

    const dailyTotals = DAY_ORDER.map(dayKey => {
      return allTimeSlots.reduce((sum, timeSlot) => sum + (hourlyUsageData[dayKey]?.[timeSlot] || 0), 0);
    });

    const grandTotal = dailyTotals.reduce((sum, val) => sum + val, 0);

    const totalRow = {
      name: 'Toplam',
      data: [
        ...DAY_ORDER.map((dayKey, index) => ({ x: DAY_MAP_TR[dayKey], y: dailyTotals[index] })),
        { x: 'Toplam', y: grandTotal }
      ]
    };

    const heatmapSeries = [
      totalRow,
      ...allTimeSlots.map((timeSlot, index) => ({
        name: timeSlot.split('-')[0],
        data: [
          ...DAY_ORDER.map(dayKey => ({ x: DAY_MAP_TR[dayKey], y: hourlyUsageData[dayKey]?.[timeSlot] || 0 })),
          { x: 'Toplam', y: hourlyTotals[index] }
        ]
      })).reverse()
    ];

    const allCounts = Object.values(hourlyUsageData).flatMap(day => (day && typeof day === 'object') ? Object.values(day) : []);
    const maxCount = allCounts.length > 0 ? Math.max(...allCounts, 0) : 10;
    const heatmapColors = ['#D7D7D7', '#93D4F4', '#46A5F9', '#4086F5', '#345CC3'];
    const heatmapChart = {
      series: heatmapSeries,
      options: {
        chart: { type: 'heatmap', toolbar: { show: false } },
        plotOptions: {
          heatmap: {
            shadeIntensity: 0.5,
            radius: 2,
            colorScale: {
              ranges: [
                { from: 0, to: 0, color: heatmapColors[0] },
                { from: 1, to: Math.floor(maxCount * 0.25), color: heatmapColors[2] },
                { from: Math.floor(maxCount * 0.25) + 1, to: Math.floor(maxCount * 0.7), color: heatmapColors[3] },
                { from: Math.floor(maxCount * 0.7) + 1, to: maxCount, color: heatmapColors[4] }
              ]
            }
          }
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['#000'],
            fontSize: '12px',
            fontWeight: 500
          }
        },
        stroke: { show: true, width: 3, colors: ['#fff'] },
        xaxis: { type: 'category' },
        legend: { show: false },
        tooltip: { y: { formatter: (value) => `${value} adet` } }
      },
      maxCount,
      colors: heatmapColors
    };

    return { 
      monthlyDeliveryChart, 
      monthlyUsageChart, 
      weeklyDeliveryChart, 
      weeklyUsageChart, 
      heatmapChart,
      priceRanges // <-- Fiyat aralıklarını ve boolean bayrakları içeren obje
    };
  }, [analyticsData]);
  // --- useMemo SONU ---

  const filteredRows = handsfreeList.filter((item) => {
    const search = filterText.toLowerCase();
    return (item.additionalNote?.toLowerCase().includes(search) || item.order_number?.toLowerCase().includes(search) || item.deliveryTypeInfo?.text?.toLowerCase().includes(search));
  });
  const paginatedRows = filteredRows.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const maskName = (name) => {
    if (!name || name.length === 0) return '*******';
    const parts = name.split(' ');
    return parts.map(part => part.length > 1 ? part.charAt(0) + '*'.repeat(part.length - 1) : part).join(' ');
  };
  const maskPhone = (phone) => {
    if (!phone || typeof phone !== 'string' || phone.length < 10) return '**********';
    const start = phone.slice(0, 3);
    const end = phone.slice(-3);
    return `${start}****${end}`;
  };

  if (loading || permissionLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}><CircularProgress /></Box>;
  }

  // Fiyat kartı için bir yardımcı değişken (okunabilirlik için)
  const priceDataExists = hasPermission && (
    charts.priceRanges.hasPoints ||
    charts.priceRanges.predefined.length > 0 ||
    charts.priceRanges.hasPostbox ||
    charts.priceRanges.hasLocalDelivery
  );

  return (
    <Box sx={{ px: 4, py: 3 }}>
      <Helmet><title>Hands Free</title></Helmet>

      {showApplyBanner && !hasPermission && (
        <Stack sx={{ width: '100%', mb: 4, mx: 'auto', borderBottom: '1px solid #D6BBFB', borderRadius: 1, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)' }}>
          <Alert severity="info" sx={{ backgroundColor: 'white', alignItems: 'center', pr: 4, '& .MuiAlert-icon': { color: theme.palette.info.main } }} action={
            <Stack direction="row" gap={1} alignItems="center">
              <Button color="primary" variant="contained" size="small" onClick={handleApplyClick} sx={{ padding: '10px 16px', cursor: 'pointer' }}>Başvur</Button>
              <IconButton aria-label="close" color="inherit" size="small" sx={{ cursor: 'pointer' }} onClick={() => { setShowApplyBanner(false); }}><Iconify icon="eva:close-fill" width={20} height={20} /></IconButton>
            </Stack>
          } onClose={() => setShowApplyBanner(false)} >
            Bu hizmet şu anda aktif değil. Grafik ve liste verilerini görüntülemek için başvuruda bulunabilirsiniz.
          </Alert>
        </Stack>
      )}

      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3} >
        <Typography variant="h5">Hands Free</Typography>
        <Button variant="text" onClick={handleOpenVideoModal} startIcon={<Iconify icon="eva:play-circle-fill" />} sx={{ textTransform: 'none' }} >Tanıtım Videosunu İzle</Button>
      </Stack>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{minHeight: 140,  p: 2, display: 'flex', alignItems: 'center', borderRadius: 2, bgcolor: 'background.paper' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">Günlük Kullanım Adedi (Bugün)</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Toplam: <strong>{analyticsData?.totalUsage ?? 0}</strong></Typography>
              <Typography variant="body2" color="text.secondary">Ortalama: <strong>{analyticsData?.averageUsage ?? 0}</strong></Typography>
            </Box>
            <Box sx={{ width: 40, height: 40, borderRadius: 1, background: 'linear-gradient(135deg, #3461D6 0%, #264697 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Iconify icon="eva:layers-outline" width={20} height={20} sx={{ color: '#fff' }} /></Box>
          </Card>
        </Grid>

        {/* --- DEĞİŞTİRİLEN FİYAT BİLGİLERİ KARTI --- */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ p: 2, minHeight: 140, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Fiyat Bilgileri</Typography>
            <Stack spacing={1}>

              {/* 1. Nokta Fiyatı (Koşullu) */}
              {hasPermission && charts.priceRanges.hasPoints && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Nokta Fiyatı:</Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary' }}>
                    {charts.priceRanges.points}
                  </Typography>
                </Box>
              )}
              
              {/* 2. Predefined Fiyatları (Koşullu - .map() boşsa render etmez) */}
              {hasPermission && charts.priceRanges.predefined.map((item) => (
                <Box 
                  key={item.type} 
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {item.type} Fiyatı:
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary' }}>
                    {item.display}
                  </Typography>
                </Box>
              ))}

              {/* 3. Postbox Fiyatı (Koşullu) */}
              {hasPermission && charts.priceRanges.hasPostbox && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Emanet Dolabı Fiyatı:</Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary' }}>
                    {charts.priceRanges.postbox}
                  </Typography>
                </Box>
              )}
              
              {/* 4. Local Delivery Fiyatı (Koşullu) */}
              {hasPermission && charts.priceRanges.hasLocalDelivery && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Adrese Teslimat Fiyatı:</Typography>
                  <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, color: 'text.secondary' }}>
                    {charts.priceRanges.localDelivery}
                  </Typography>
                </Box>
              )}

              {/* --- YEDEK DURUMLAR --- */}

              {/* Durum 1: İzin var, ancak HİÇBİR fiyat verisi yok */}
              {!priceDataExists && hasPermission && (
                <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                  Fiyat bilgisi bulunmamaktadır.
                </Typography>
              )}
              
              {/* Durum 2: İzin yok */}
              {!hasPermission && (
                <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                  Fiyat bilgilerini görmek için hizmete başvurunuz.
                </Typography>
              )}

            </Stack>
          </Card>
        </Grid>
        {/* --- KART DEĞİŞİKLİĞİ SONU --- */}

      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', height: 360 }} elevation={0}>
            <Typography variant="h6" gutterBottom>Aylık Teslimat Tercihine Göre Kullanımı</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Taşıma Adedi</Typography>
            <ReactApexChart options={charts.monthlyDeliveryChart.options} series={hasPermission ? charts.monthlyDeliveryChart.series : zeroOutSeries(charts.monthlyDeliveryChart.series)} type="bar" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', height: 360 }} elevation={0}>
            <Typography variant="h6" gutterBottom>Aylık Kullanım Adedi ve Ciro Hareketleri</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={charts.monthlyUsageChart.options} series={hasPermission ? charts.monthlyUsageChart.series : zeroOutSeries(charts.monthlyUsageChart.series)} type="line" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', height: 360 }} elevation={0}>
            <Typography variant="h6" gutterBottom>Haftalık Teslimat Tercihine Göre Kullanımı</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Taşıma Adedi</Typography>
            <ReactApexChart options={charts.weeklyDeliveryChart.options} series={hasPermission ? charts.weeklyDeliveryChart.series : zeroOutSeries(charts.weeklyDeliveryChart.series)} type="bar" height={260} />
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper', height: 360 }} elevation={0}>
            <Typography variant="h6" gutterBottom>Haftalık Kullanım Adedi ve Ciro Hareketleri</Typography>
            <Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="p" gutterBottom>Toplam Kullanım Adedi - Toplam Ciro</Typography>
            <ReactApexChart options={charts.weeklyUsageChart.options} series={hasPermission ? charts.weeklyUsageChart.series : zeroOutSeries(charts.weeklyUsageChart.series)} type="line" height={260} />
          </Card>
        </Grid>
      </Grid>

      <Grid item xs={12} sx={{ mt: 3 }}>
        <Card sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }} elevation={0}>
          <Box mb={1}><Typography variant="h6">Gün ve Saat Bazlı Handsfree Kullanım Hareketleri</Typography><Typography sx={{ color: '#4B5563', fontSize: '14px' }} variant="body2">Kullanım Yoğunluğu</Typography></Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ flex: 1 }}><ReactApexChart options={charts.heatmapChart.options} series={hasPermission ? charts.heatmapChart.series : zeroOutSeries(charts.heatmapChart.series)} type="heatmap" height={320} /></Box>
            <HeatmapLegend maxCount={charts.heatmapChart.maxCount} colors={charts.heatmapChart.colors} label="Hands Free Kullanım Adedi" />
          </Stack>
        </Card>
      </Grid>

      <Paper sx={{ mt: 3, p: 3, bgcolor: 'background.paper' }} elevation={0}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}><TextField fullWidth placeholder="Ara..." value={filterText} onChange={(e) => setFilterText(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }} /></Grid>
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Başlangıç Tarihi" value={startDate} onChange={setStartDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2}><LocalizationProvider dateAdapter={AdapterMoment}><DatePicker label="Bitiş Tarihi" value={endDate} onChange={setEndDate} renderInput={(params) => <TextField {...params} fullWidth size="small" />} /></LocalizationProvider></Grid>
          <Grid item xs={6} md={2}><Button fullWidth variant="outlined" size="medium" onClick={handleClearFilters} disabled={!hasPermission}>TEMİZLE</Button></Grid>
          <Grid item xs={6} md={2}><Button fullWidth variant="contained" size="medium" onClick={getLiveAnalyticsHandsfreeList} disabled={!hasPermission}>LİSTELE</Button></Grid>
        </Grid>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead sx={{ background: 'background.paper' }}><TableRow>{['No', 'Ad Soyad', 'Telefon', 'Tarih', 'Durum'].map((h) => (<TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>))}</TableRow></TableHead>
            <TableBody>
              {paginatedRows.map((r, index) => (
                <TableRow key={r.id} hover>
                  <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell><TableCell>{maskName(r.additionalNote)}</TableCell><TableCell>{maskPhone(r.phone)}</TableCell>
                  <TableCell>{new Date(Number(r.createdCateTime) * 1000).toLocaleString('tr-TR')}</TableCell>
                  <TableCell>{r.status === "1" ? "Tamamlandı" : r.status === "2" ? "İptal Edildi" : r.status === "3" ? "Bekliyor" : r.status === "4" ? "Porter Üzerinde" : r.status === "5" ? "Şubede" : r.status === "6" ? "Müşteri Tarafından Onay Bekliyor" : r.status === "7" ? "Porter Gönderiyi Şubeye Bıraktı" : r.status === "8" ? "Kurye Bekliyor" : r.status === "9" ? "Kuryede" : "Postbox'ta"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow><TableCell colSpan={5} sx={{ py: 2 }}><Grid container alignItems="center"><Grid item xs><Typography variant="caption">Toplam {filteredRows.length} kayıt</Typography></Grid><Grid item><Pagination count={Math.ceil(filteredRows.length / rowsPerPage)} page={page} onChange={(e, val) => setPage(val)} shape="rounded" size="small" /></Grid></Grid></TableCell></TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={isVideoModalOpen} onClose={handleCloseVideoModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2 }}>Tanıtım Videosu<IconButton aria-label="close" onClick={handleCloseVideoModal} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers><video width="100%" height="auto" controls autoPlay src="https://files.easypoint.com.tr/files/data-ekranlari/hands-free-2.vs.mp4">Tarayıcınız video etiketini desteklemiyor.</video></DialogContent>
      </Dialog>

      <Dialog open={openApplyDialog} onClose={handleCloseDialog}>
        <DialogTitle>Hizmet Başvurusu</DialogTitle>
        <DialogContent><DialogContentText>Bu hizmetle ilgili bilgi alma isteğiniz Genel Merkezimize iletilecektir. Onaylıyor musunuz?</DialogContentText></DialogContent>
        <DialogActions><Button onClick={handleCloseDialog}>İptal</Button><Button onClick={handleConfirmApply} autoFocus>Onayla</Button></DialogActions>
      </Dialog>
    </Box>
  );
}